import {Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {BehaviorSubject, firstValueFrom, Observable, Subject, Subscription} from 'rxjs';
import {CurrentUserViewModel} from '../../../core/models/current-user.view-model';
import {VehicleTypeEnum} from '../../../core/enums/vehicle-type.enum';
import {ResponseNotificationService} from '../../../core/services/response-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {FiltersDataProviderService} from '../../../shared/services/filters.data-provider.service';
import {VehicleInterface} from '../../../shared/modal/components/add-vehicle/vehicle.interface';
import {ProjectCreationService} from './project-creation.service';
import {MobileBackCoordinatorService} from '../../../core/mobile/mobile-back-coordinator.service';
import {VehicleViewModel} from './vehicle.view-model';
import {ModalInterface} from '../../../shared/modal/modal.interface';
import type {ModalComponent} from '../../../shared/modal/modal.component';
import {ModalState} from '../../../shared/modal/modal-state.enum';
import {ModalStateEvent} from '../../../shared/modal/modal-state-event';
import {
  EMPTY_VEHICLE_REGISTRATION_DATA,
  isVehicleRegistrationDataValid,
  normalizeVehicleRegistrationData,
  VehicleRegistrationData,
} from './vehicle-registration-data';

type WizardStep = 'vehicleType' | 'make' | 'model' | 'bodyType' | 'bodyGen' | 'edition' | 'transmission' | 'review';
type CatalogStep = Exclude<WizardStep, 'review'>;
type DraftPrompt = 'exit' | 'resume';
type CatalogItem = {id?: number; name?: string; displayName?: string; code?: string; ['@id']?: string};
interface WizardViewState {
  step: WizardStep;
  items: CatalogItem[];
  selectedVehicle: VehicleViewModel | null;
  query: string;
  loading: boolean;
  error: boolean;
  noMatch: boolean;
  publishing: boolean;
  draftPrompt: DraftPrompt | null;
  changePrompt: boolean;
  registration: VehicleRegistrationData;
}

const CATALOG_STEPS: CatalogStep[] = ['vehicleType', 'make', 'model', 'bodyType', 'bodyGen', 'edition', 'transmission'];
const WIZARD_STEPS: WizardStep[] = [...CATALOG_STEPS, 'review'];

/**
 * Responsive vehicle wizard rendered inside the application's single
 * ModalService host, both in browsers and in the Capacitor fallback.
 */
@Component({
  selector: 'app-web-vehicle-wizard',
  templateUrl: './web-vehicle-wizard.component.html',
  styleUrls: ['./web-vehicle-wizard.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WebVehicleWizardComponent implements OnInit, OnDestroy, ModalInterface {
  public data!: {currentUser: CurrentUserViewModel};
  public parent?: ModalComponent;
  public subject!: Subject<ModalStateEvent>;
  public state!: Observable<ModalStateEvent>;
  public currentUser!: CurrentUserViewModel;
  private readonly viewStateSubject = new BehaviorSubject<WizardViewState>({
    step: 'vehicleType', items: [], selectedVehicle: null, query: '', loading: false,
    error: false, noMatch: false, publishing: false, draftPrompt: null, changePrompt: false,
    registration: {...EMPTY_VEHICLE_REGISTRATION_DATA},
  });
  public readonly viewState$ = this.viewStateSubject.asObservable();
  public selected: Partial<Record<CatalogStep, CatalogItem>> = {};
  @ViewChild('stepper') private stepper?: ElementRef<HTMLElement>;
  @ViewChild('content') private content?: ElementRef<HTMLElement>;
  private requestId = 0;
  private publishSubscription?: Subscription;
  private modalShown = false;
  private sessionStarted = false;
  /**
   * The first list is part of the modal's initial view, not a navigation
   * side-effect. Keeping this separate from the dialog animation means a
   * missed PrimeNG onShow can never leave the first step empty.
   */
  private vehicleTypesPreloaded = false;
  private destroyed = false;
  private stepperAlignmentFrame: number | null = null;
  private selectedItemAlignmentFrame: number | null = null;
  private listDrag?: {pointerId: number; startY: number; startScrollTop: number; moved: boolean; surface: HTMLElement; scrollContainer: HTMLElement};
  private suppressItemClick = false;
  private resolveDraftPrompt?: (decision: boolean) => void;
  private pendingChoiceChange?: {step: CatalogStep; item: CatalogItem; resolve: (decision: boolean) => void};
  private releaseNativeBackHandler?: () => void;

  constructor(
    private readonly catalog: FiltersDataProviderService,
    private readonly projectCreation: ProjectCreationService,
    private readonly notifications: ResponseNotificationService,
    private readonly translate: TranslateService,
    private readonly mobileBack: MobileBackCoordinatorService,
  ) {}

  public get step(): WizardStep { return this.viewStateSubject.value.step; }
  public get items(): CatalogItem[] { return this.viewStateSubject.value.items; }
  public get selectedVehicle(): VehicleViewModel | null { return this.viewStateSubject.value.selectedVehicle; }
  public get query(): string { return this.viewStateSubject.value.query; }
  /**
   * Kept writable for an already-emitted Angular HMR template. The current
   * template calls updateQuery(), but this setter lets a stale template finish
   * compiling instead of pinning the dev server to its last successful view.
   */
  public set query(value: string) { this.updateQuery(value); }
  public get loading(): boolean { return this.viewStateSubject.value.loading; }
  public get error(): boolean { return this.viewStateSubject.value.error; }
  public get noMatch(): boolean { return this.viewStateSubject.value.noMatch; }
  public get publishing(): boolean { return this.viewStateSubject.value.publishing; }
  public get registration(): VehicleRegistrationData { return this.viewStateSubject.value.registration; }

  public ngOnInit(): void {
    this.currentUser = this.data.currentUser;
    if (typeof window !== 'undefined' && Capacitor.isNativePlatform()) {
      this.releaseNativeBackHandler = this.mobileBack.register('web-vehicle-wizard', async () => {
        if (!this.destroyed && !this.publishing) {
          await this.back();
        }
        return true;
      });
    }
    this.configureModalFrame();
    this.updateModalTitle();
    this.preloadVehicleTypes();
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.resolveDraftPrompt?.(false);
    this.resolveDraftPrompt = undefined;
    this.resolveChoiceChange(false);
    this.releaseModalResources();
    this.viewStateSubject.complete();
  }

  public releaseModalResources(): void {
    this.resolveChoiceChange(false);
    this.releaseNativeBackHandler?.();
    this.releaseNativeBackHandler = undefined;
    this.requestId++;
    this.publishSubscription?.unsubscribe();
    if (this.stepperAlignmentFrame !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.stepperAlignmentFrame);
      this.stepperAlignmentFrame = null;
    }
    if (this.selectedItemAlignmentFrame !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.selectedItemAlignmentFrame);
      this.selectedItemAlignmentFrame = null;
    }
    this.listDrag?.surface.classList.remove('is-dragging');
    this.listDrag = undefined;
    this.suppressItemClick = false;
  }

  public onModalShown(): void {
    this.modalShown = true;
    this.configureModalFrame();
    this.updateModalTitle();
    this.alignStepper();
    void this.startSessionIfReady();
  }

  public async beforeModalCloseAttempt(): Promise<boolean> {
    if (this.hasOpenPrompt()) {
      return false;
    }
    if (this.publishing || !Object.keys(this.selected).length) {
      return !this.publishing;
    }

    const saveDraft = await this.askDraftDecision('exit');
    if (saveDraft) {
      this.saveDraft(this.step);
    } else {
      this.clearDraft();
    }
    return true;
  }

  public resolveDraftDecision(decision: boolean): void {
    const resolve = this.resolveDraftPrompt;
    this.resolveDraftPrompt = undefined;
    this.patchViewState({draftPrompt: null});
    resolve?.(decision);
  }

  public get visibleSteps(): WizardStep[] {
    const steps: WizardStep[] = ['vehicleType', 'make', 'model', 'bodyType'];
    if (this.selected.vehicleType?.code === VehicleTypeEnum.CAR) {
      steps.push('bodyGen');
    }
    return [...steps, 'edition', 'transmission', 'review'];
  }

  public get filteredItems(): CatalogItem[] {
    const normalized = this.query.trim().toLocaleLowerCase();
    return normalized ? this.items.filter((item) => this.label(item).toLocaleLowerCase().includes(normalized)) : this.items;
  }

  public get choices(): Array<{step: CatalogStep; item: CatalogItem}> {
    return CATALOG_STEPS
      .filter((step) => !!this.selected[step])
      .map((step) => ({step, item: this.selected[step] as CatalogItem}));
  }

  public label(item: CatalogItem | null | undefined): string {
    return item?.displayName || item?.name || '';
  }

  public stepLabel(step: WizardStep): string {
    return `features.user.garage.mobile_wizard.catalog.${step}`;
  }

  public isSelected(item: CatalogItem): boolean {
    return this.selected[this.step as CatalogStep]?.id === item.id;
  }

  public canNavigate(step: WizardStep): boolean {
    const index = this.visibleSteps.indexOf(step);
    return !this.loading && !this.hasOpenPrompt() && index >= 0
      && this.visibleSteps.slice(0, index).every((previous) => previous === 'review' || !!this.selected[previous as CatalogStep]);
  }

  public async choose(item: CatalogItem): Promise<void> {
    if (this.loading || this.step === 'review' || this.hasOpenPrompt()) {
      return;
    }
    const currentStep = this.step as CatalogStep;
    const current = this.selected[currentStep];
    const currentKey = current?.['@id'] || current?.id;
    const itemKey = item['@id'] || item.id;
    const changed = !currentKey || !itemKey || currentKey !== itemKey;
    if (changed && this.hasDownstreamSelection(currentStep)) {
      const confirmed = await this.askChoiceChange(currentStep, item);
      if (!confirmed) {
        return;
      }
    }
    this.selected[currentStep] = item;
    if (changed) {
      this.clearAfter(currentStep);
    }
    const next = this.nextStep(this.step);
    this.saveDraft(next);
    await this.loadStep(next);
  }

  public async goTo(step: WizardStep): Promise<void> {
    if (this.canNavigate(step)) {
      this.saveDraft(step);
      await this.loadStep(step);
    }
  }

  public resolveChoiceChange(decision: boolean): void {
    const pending = this.pendingChoiceChange;
    this.pendingChoiceChange = undefined;
    this.patchViewState({changePrompt: false});
    pending?.resolve(decision);
  }

  public beginListDrag(event: PointerEvent): void {
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      return;
    }
    const surface = event.currentTarget as HTMLElement;
    const scrollContainer = this.content?.nativeElement;
    if (!scrollContainer) {
      return;
    }
    this.listDrag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startScrollTop: scrollContainer.scrollTop,
      moved: false,
      surface,
      scrollContainer,
    };
  }

  public moveListDrag(event: PointerEvent): void {
    const drag = this.listDrag;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const offset = event.clientY - drag.startY;
    if (!drag.moved && Math.abs(offset) < 4) {
      return;
    }
    if (!drag.moved) {
      drag.moved = true;
      drag.surface.setPointerCapture?.(event.pointerId);
    }
    drag.surface.classList.add('is-dragging');
    drag.scrollContainer.scrollTop = drag.startScrollTop - offset;
    event.preventDefault();
  }

  public endListDrag(event: PointerEvent): void {
    const drag = this.listDrag;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    drag.surface.classList.remove('is-dragging');
    if (drag.surface.hasPointerCapture?.(event.pointerId)) {
      drag.surface.releasePointerCapture(event.pointerId);
    }
    this.listDrag = undefined;
    if (!drag.moved) {
      return;
    }
    this.suppressItemClick = true;
    window.setTimeout(() => this.suppressItemClick = false);
  }

  public chooseCatalogItem(event: MouseEvent, item: CatalogItem): void {
    if (this.suppressItemClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    void this.choose(item);
  }

  public async back(): Promise<void> {
    if (this.publishing || this.hasOpenPrompt()) {
      return;
    }
    const index = this.visibleSteps.indexOf(this.step);
    if (index <= 0) {
      await this.close();
      return;
    }
    await this.goTo(this.visibleSteps[index - 1]);
  }

  /**
   * The shared ModalComponent owns the actual PrimeNG dialog. The wizard asks
   * whether to retain or discard the local draft before requesting dismissal.
   */
  public async close(): Promise<void> {
    if (await this.beforeModalCloseAttempt()) {
      this.parent?.requestHide(true);
    }
  }

  public retry(): void {
    void this.loadStep(this.step);
  }

  public publish(): void {
    if (!this.selectedVehicle || this.publishing) {
      return;
    }
    const registration = normalizeVehicleRegistrationData(this.registration);
    this.patchViewState({registration});
    if (!isVehicleRegistrationDataValid(registration)) {
      this.notifications.emitMessage(this.translate.instant('features.user.garage.mobile_wizard.registration.invalid_date'));
      return;
    }
    this.patchViewState({publishing: true});
    this.publishSubscription = this.projectCreation.create(this.currentUser, this.selectedVehicle, registration).subscribe({
      next: (project) => {
        this.clearDraft();
        this.patchViewState({publishing: false});
        this.notifications.emitMessage(this.translate.instant('features.user.garage.project_create.success', {vehicleName: project.vehicle.fullVehicleIdentity}));
        this.subject.next(new ModalStateEvent(ModalState.SUCCESS, project));
      },
      error: () => {
        this.patchViewState({publishing: false});
        this.notifications.emitMessage(this.translate.instant('features.user.garage.project_create.error'));
      },
    });
  }

  private async startSessionIfReady(): Promise<void> {
    if (!this.modalShown || this.sessionStarted || !this.currentUser) {
      return;
    }
    this.sessionStarted = true;
    if (this.destroyed) {
      return;
    }
    const draft = this.readDraft();
    if (!draft) {
      this.preloadVehicleTypes();
      return;
    }
    await this.resumeDraft(draft);
  }

  private async resumeDraft(draft: {step: WizardStep; selected: Partial<Record<CatalogStep, CatalogItem>>; registration?: Partial<VehicleRegistrationData>}): Promise<void> {
    const confirmed = await this.askDraftDecision('resume');
    if (confirmed) {
      this.selected = draft.selected;
      this.patchViewState({registration: normalizeVehicleRegistrationData(draft.registration)});
      await this.loadStep(this.isReachable(draft.step) ? draft.step : 'vehicleType');
      return;
    }
    this.clearDraft();
    await this.loadStep('vehicleType');
  }

  private askDraftDecision(prompt: DraftPrompt): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.resolveDraftPrompt = resolve;
      this.patchViewState({draftPrompt: prompt});
    });
  }

  /** Keeps a destructive catalog change inside the wizard modal's own layer. */
  private askChoiceChange(step: CatalogStep, item: CatalogItem): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.pendingChoiceChange = {step, item, resolve};
      this.patchViewState({changePrompt: true});
    });
  }

  private hasOpenPrompt(): boolean {
    const viewState = this.viewStateSubject.value;
    return !!viewState.draftPrompt || viewState.changePrompt;
  }

  private async loadStep(step: WizardStep): Promise<void> {
    const request = ++this.requestId;
    this.patchViewState({
      step,
      query: '',
      items: [],
      error: false,
      noMatch: false,
      selectedVehicle: null,
      loading: true,
    });
    this.updateModalTitle();
    this.alignStepper();
    try {
      if (step === 'review') {
        const vehicles = await firstValueFrom(this.catalog.getVehicleByParams(this.vehicleParams()));
        if (request !== this.requestId) {
          return;
        }
        const selectedVehicle = vehicles[0] || null;
        if (selectedVehicle) {
          selectedVehicle.vehicleType = this.selected.vehicleType as any;
        }
        this.patchViewState({selectedVehicle, noMatch: !selectedVehicle, loading: false});
        return;
      }
      const items = await firstValueFrom(this.itemsFor(step));
      if (request !== this.requestId) {
        return;
      }
      if (!items.length && step === 'edition') {
        await this.loadStep('transmission');
        return;
      }
      if (!items.length && step === 'transmission') {
        await this.loadStep('review');
        return;
      }
      this.patchViewState({items: this.uniqueItems(items), loading: false});
      this.alignSelectedItem(step);
    } catch {
      if (request === this.requestId) {
        this.patchViewState({error: true, loading: false});
      }
    }
  }

  /** Starts the initial catalogue request as soon as the wizard is created. */
  private preloadVehicleTypes(): void {
    if (this.vehicleTypesPreloaded || this.destroyed) {
      return;
    }
    this.vehicleTypesPreloaded = true;
    void this.loadStep('vehicleType');
  }

  private itemsFor(step: CatalogStep): Observable<CatalogItem[]> {
    const vehicleType = this.selected.vehicleType as any;
    const make = this.selected.make as any;
    const model = this.selected.model as any;
    const bodyType = this.selected.bodyType as any;
    const bodyGen = this.selected.bodyGen as any;
    switch (step) {
      case 'vehicleType': return this.catalog.getVehicleTypeCollection();
      case 'make': return this.catalog.getMakeCollection([vehicleType]);
      case 'model': return this.catalog.getModelCollection([[vehicleType], [make]]);
      case 'bodyType': return this.catalog.getBodyTypeCollection([[vehicleType], [make], [model]]);
      case 'bodyGen': return this.catalog.getBodyGenCollection([[vehicleType], [make], [model], [bodyType]]);
      case 'edition': return this.catalog.getEditionCollection(bodyGen ? [[vehicleType], [make], [model], [bodyType], [bodyGen]] : [[vehicleType], [make], [model], [bodyType]]);
      case 'transmission': return this.catalog.getTransmissionCollectionByVehicleParams(this.vehicleParams());
    }
    throw new Error(`Unsupported catalogue step: ${step}`);
  }

  private nextStep(step: CatalogStep): WizardStep {
    const steps = this.visibleSteps;
    return steps[steps.indexOf(step) + 1] || 'review';
  }

  private hasDownstreamSelection(step: CatalogStep): boolean {
    return CATALOG_STEPS.slice(CATALOG_STEPS.indexOf(step) + 1).some((next) => !!this.selected[next]);
  }

  private clearAfter(step: CatalogStep): void {
    CATALOG_STEPS.slice(CATALOG_STEPS.indexOf(step) + 1).forEach((next) => delete this.selected[next]);
  }

  private isReachable(step: WizardStep): boolean {
    const index = this.visibleSteps.indexOf(step);
    return index >= 0 && this.visibleSteps.slice(0, index).every((previous) => previous === 'review' || !!this.selected[previous as CatalogStep]);
  }

  private vehicleParams(): VehicleInterface {
    return {
      make: this.selected.make as any, model: this.selected.model as any, bodyType: this.selected.bodyType as any,
      bodyGen: this.selected.bodyGen as any, edition: this.selected.edition as any, transmission: this.selected.transmission as any,
    };
  }

  private get draftKey(): string {
    return `wtg.web.vehicle-wizard.${this.currentUser?.['@id'] || 'anonymous'}`;
  }

  public updateRegistration(field: keyof VehicleRegistrationData, value: string): void {
    this.patchViewState({registration: normalizeVehicleRegistrationData({...this.registration, [field]: value})});
    this.saveDraft(this.step);
  }

  public get registrationDate(): Date | null {
    const value = this.registration.firstRegistrationInCountry;
    if (!value) {
      return null;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
      return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const date = new Date(year, month, day);
    return Number.isNaN(date.getTime()) || date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day
      ? null
      : date;
  }

  public updateRegistrationDate(value: Date | null): void {
    if (!value) {
      this.updateRegistration('firstRegistrationInCountry', '');
      return;
    }
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    this.updateRegistration('firstRegistrationInCountry', `${value.getFullYear()}-${month}-${day}`);
  }

  public updateQuery(value: string): void {
    this.patchViewState({query: value});
  }

  public get registrationDataValid(): boolean {
    return isVehicleRegistrationDataValid(this.registration);
  }

  private saveDraft(nextStep: WizardStep): void {
    try {
      localStorage.setItem(this.draftKey, JSON.stringify({
        version: 2,
        step: nextStep,
        selected: this.selected,
        registration: normalizeVehicleRegistrationData(this.registration),
      }));
    } catch {
      // A full or unavailable browser store must never block catalogue
      // navigation. The wizard remains usable, only draft persistence is lost.
    }
  }

  private readDraft(): {step: WizardStep; selected: Partial<Record<CatalogStep, CatalogItem>>; registration?: Partial<VehicleRegistrationData>} | null {
    try {
      const draft = JSON.parse(localStorage.getItem(this.draftKey) || 'null');
      return draft && WIZARD_STEPS.includes(draft.step) && draft.selected ? draft : null;
    } catch {
      return null;
    }
  }

  private clearDraft(): void {
    try {
      localStorage.removeItem(this.draftKey);
    } catch {
      // See saveDraft(): storage availability must not block the flow.
    }
  }

  private configureModalFrame(): void {
    if (!this.parent) {
      return;
    }
    this.parent.hasHeaderBackButton = true;
    this.parent.disableHeaderBackButton = false;
    this.parent.headerBackCallback = () => void this.back();
    this.parent.headerCloseCallback = () => void this.close();
  }

  private uniqueItems(items: CatalogItem[]): CatalogItem[] {
    const seen = new Set<string>();
    return items.filter((item) => {
      const key = item['@id'] || String(item.id || '');
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private updateModalTitle(): void {
    this.parent?.updateName(this.translate.instant(this.stepLabel(this.step)));
  }

  /** Centers the active stage without creating a second gesture owner. */
  private alignStepper(): void {
    if (typeof requestAnimationFrame === 'undefined') {
      return;
    }
    if (this.stepperAlignmentFrame !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.stepperAlignmentFrame);
    }
    this.stepperAlignmentFrame = requestAnimationFrame(() => {
      this.stepperAlignmentFrame = null;
      const activeStep = this.stepper?.nativeElement.querySelector('.web-vehicle-wizard__step.is-active') as HTMLElement | null;
      const rail = this.stepper?.nativeElement;
      if (!activeStep || !rail) {
        return;
      }
      rail.scrollTo({
        left: Math.max(0, activeStep.offsetLeft - ((rail.clientWidth - activeStep.offsetWidth) / 2)),
        behavior: 'smooth',
      });
    });
  }

  /** Centers the existing choice when the user returns to a catalogue step. */
  private alignSelectedItem(step: WizardStep): void {
    if (step === 'review' || !this.selected[step as CatalogStep] || typeof requestAnimationFrame === 'undefined') {
      return;
    }
    if (this.selectedItemAlignmentFrame !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.selectedItemAlignmentFrame);
    }
    this.selectedItemAlignmentFrame = requestAnimationFrame(() => {
      this.selectedItemAlignmentFrame = null;
      if (this.step !== step) {
        return;
      }
      const content = this.content?.nativeElement;
      const selected = content?.querySelector('.web-vehicle-wizard__item.is-selected') as HTMLElement | null;
      if (!content || !selected) {
        return;
      }
      const contentBounds = content.getBoundingClientRect();
      const selectedBounds = selected.getBoundingClientRect();
      content.scrollTo({
        top: Math.max(0, content.scrollTop + selectedBounds.top - contentBounds.top - ((content.clientHeight - selectedBounds.height) / 2)),
        behavior: 'auto',
      });
    });
  }

  private patchViewState(patch: Partial<WizardViewState>): void {
    if (!this.destroyed) {
      this.viewStateSubject.next({...this.viewStateSubject.value, ...patch});
    }
  }

}
